import React, {useRef} from 'react';
import useDialogA11y from '../../hooks/useDialogA11y';

/**
 * Каркас диалога подтверждения для раздела «Выбытие».
 *
 * <p>Необратимое действие обязано заранее показать точный состав и последствия, поэтому в
 * диалоге есть отдельное место под перечень и под фразу о том, что именно случится. Клавиатурный
 * контракт (Escape, ловушка фокуса, возврат фокуса) — общий хук, как у остальных модалок.</p>
 */
export default function DisposalDialog({title, tone = 'brand', confirmLabel, onConfirm, onCancel, children}: {
    title: string;
    /** `danger` — для того, что нельзя отменить и что уничтожает данные. */
    tone?: 'brand' | 'danger';
    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
    children: React.ReactNode;
}) {
    const ref = useRef<HTMLDivElement>(null);
    useDialogA11y(ref, onCancel);
    const confirmClass = tone === 'danger'
        ? 'bg-rose-600 hover:bg-rose-700'
        : 'bg-brand-600 hover:bg-brand-700';

    return (
        <div className="fw-overlay fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel}
                 data-testid="disposal-dialog-backdrop"/>
            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                data-testid="disposal-dialog"
                className="relative z-10 w-full max-w-md bg-white dark:bg-ink-900 rounded-2xl shadow-xl
                           p-6 space-y-4 outline-none max-h-[85vh] overflow-y-auto"
            >
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">{children}</div>
                <div className="flex flex-wrap justify-end gap-3 pt-2">
                    <button type="button" onClick={onCancel}
                            className="btn-secondary min-h-[44px] px-4 rounded-xl text-sm">
                        Отмена
                    </button>
                    <button type="button" onClick={onConfirm} data-testid="disposal-dialog-confirm"
                            className={`min-h-[44px] px-4 rounded-xl text-sm font-medium text-white
                                        transition-colors ${confirmClass}`}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
